import { useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Bell, CheckCircle2, XCircle, Clock, Volume2, VolumeX } from "lucide-react";
import { doses as dosesApi } from "../lib/api";
import { playAlarm, stopAlarm, resolveAlarm } from "../lib/alarm";
import { toast } from "sonner";

export default function ReminderPopup({ data, onClose }) {
  const rxId = data?.prescription?.id;
  const ringingRef = useRef(false);
  const cfg = rxId ? resolveAlarm(rxId) : null;

  useEffect(() => {
    if (!data || !cfg || !cfg.enabled) return;
    // Try to ring. If autoplay is blocked (rare — usually works since popup opens from polling loop during active session), the "Ring now" button below will re-trigger.
    playAlarm(rxId).then(() => { ringingRef.current = true; }).catch(() => {});
    return () => { stopAlarm(); ringingRef.current = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.dose?.id]);

  async function mark(status) {
    stopAlarm();
    try {
      await dosesApi.update(data.dose.id, status);
      toast.success(`Marked ${status}`);
      onClose();
    } catch { toast.error("Failed"); }
  }

  async function snooze() {
    stopAlarm();
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/doses/${data.dose.id}/snooze?minutes=10`, { method: "POST" });
      if (res.ok) {
        toast.success("Snoozed · reminder in 10 minutes");
        onClose();
      } else {
        toast.error("Could not snooze");
      }
    } catch { toast.error("Could not snooze"); }
  }

  async function ringNow() {
    await playAlarm(rxId);
    ringingRef.current = true;
  }

  if (!data) return null;
  const { prescription, dose } = data;
  const perDayDose = (dose?.dose_label && dose?.in_taper) ? dose.dose_label : null;
  const dayInfo = dose?.day_offset ? `Day ${dose.day_offset}${dose.week_number ? ` · Week ${dose.week_number}` : ""}` : null;

  return (
    <Dialog open={!!data} onOpenChange={(o) => { if (!o) { stopAlarm(); onClose(); } }}>
      <DialogContent className="max-w-md" data-testid="reminder-popup">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight flex items-center gap-2">
            <div className="h-9 w-9 rounded-lg bg-[color:var(--warning-bg)] flex items-center justify-center pulse-accent">
              <Bell className="h-5 w-5 text-[color:var(--warning)]" />
            </div>
            Time to take {prescription.medicine_name}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-sm"><Clock className="h-4 w-4 text-[color:var(--primary-ink)]" /> {new Date(dose.scheduled_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div>
          {perDayDose ? (
            <div
              data-testid="reminder-per-day-dose"
              className="rounded-xl border-2 p-4"
              style={{
                borderColor: "var(--primary)",
                background: "color-mix(in srgb, var(--primary) 8%, transparent)",
              }}
            >
              <div className="flex items-center gap-2 mb-1">
                <span className="text-[10px] uppercase tracking-wider font-bold" style={{ color: "var(--primary-ink)" }}>
                  Take today
                </span>
                {dayInfo && (
                  <span
                    data-testid="reminder-day-chip"
                    className="text-[10px] font-semibold px-2 py-0.5 rounded-full"
                    style={{ background: "var(--primary-soft)", color: "var(--primary-ink)" }}
                  >
                    {dayInfo}
                  </span>
                )}
              </div>
              <div className="font-display font-black text-2xl tracking-tight text-[color:var(--text-main)]" data-testid="reminder-dose-amount">
                {perDayDose}
              </div>
              {prescription.instructions && (
                <div className="text-xs mt-1.5" style={{ color: "var(--text-muted)" }}>
                  {prescription.instructions}
                </div>
              )}
              <div className="text-[11px] mt-2 italic" style={{ color: "var(--text-muted)" }}>
                Tapering schedule — this amount changes as you progress.
              </div>
            </div>
          ) : (
            <div className="text-sm">
              <span className="font-semibold">{dose?.dose_label || prescription.dosage}</span>
              {prescription.instructions ? ` · ${prescription.instructions}` : ""}
            </div>
          )}
          {cfg?.enabled && (
            <div className="flex items-center gap-2 pt-2">
              <Button size="sm" variant="ghost" onClick={() => { stopAlarm(); ringingRef.current = false; }} data-testid="reminder-mute" className="text-[color:var(--text-muted)]">
                <VolumeX className="h-3 w-3 mr-1" /> Mute
              </Button>
              <Button size="sm" variant="ghost" onClick={ringNow} data-testid="reminder-ring" className="text-[color:var(--primary)]">
                <Volume2 className="h-3 w-3 mr-1" /> Ring
              </Button>
              <span className="text-xs text-[color:var(--text-muted)] ml-auto">Tone: {cfg.tone}</span>
            </div>
          )}
        </div>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={() => mark("skipped")} data-testid="reminder-skip" className="border-[color:var(--border-subtle)]">
            <XCircle className="h-4 w-4 mr-2 text-[color:var(--accent-ink)]" /> Skip
          </Button>
          <Button variant="outline" onClick={snooze} data-testid="reminder-snooze" className="border-[color:var(--border-subtle)]">
            <Clock className="h-4 w-4 mr-2 text-[color:var(--warning)]" /> +10 min
          </Button>
          <Button onClick={() => mark("taken")} data-testid="reminder-taken" className="bg-[color:var(--primary)] hover:bg-[color:var(--primary-hover)] text-white">
            <CheckCircle2 className="h-4 w-4 mr-2" /> Mark taken
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
